import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { History, Trash2 } from "lucide-react";
import type { AiPromptVersion } from "@/types/ai-prompt";
import { formatVersionDate } from "./utils";
import { TruncatedText } from "@/components/ui/truncated-text";

type VersionHistoryListProps = {
  activeVersion: AiPromptVersion | null;
  sortedVersions: AiPromptVersion[];
  onViewDiff: (version: AiPromptVersion) => void;
  onDeleteVersion: (version: AiPromptVersion) => void;
};

export function VersionHistoryList({
  activeVersion,
  sortedVersions,
  onViewDiff,
  onDeleteVersion,
}: VersionHistoryListProps) {
  return (
    <div className="divide-y divide-border">
      <h3 className="text-base font-semibold text-foreground flex items-center gap-2 pb-3">
        <History className="h-4 w-4 text-muted-foreground" />
        Version History
      </h3>

      {activeVersion === null && (
        <div className="flex items-center justify-between gap-4 py-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <p className="text-sm font-semibold text-foreground min-w-0">
                <TruncatedText>Current version</TruncatedText>
              </p>
              <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-purple-100 text-purple-700 dark:bg-purple-500/15 dark:text-purple-400">
                Live
              </span>
            </div>
            <p className="text-xs text-muted-foreground mt-0.5">System default</p>
          </div>
        </div>
      )}

      {sortedVersions.map((version) => (
        <div
          key={version.id}
          className="flex items-center justify-between gap-4 py-3"
        >
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-sm font-semibold text-foreground min-w-0">
                <TruncatedText>{version.is_active ? "Current version" : version.name}</TruncatedText>
              </p>
              {version.is_active && (
                <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-purple-100 text-purple-700 dark:bg-purple-500/15 dark:text-purple-400">
                  Live
                </span>
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-0.5">
              {version.updated_by_name ? (
                <>{version.updated_by_name} · {formatVersionDate(version.created_at)}</>
              ) : (
                formatVersionDate(version.created_at)
              )}
            </p>
          </div>
          {!version.is_active && (
            <div className="flex items-center gap-2 shrink-0">
              <button
                type="button"
                className="text-sm font-medium text-primary hover:text-primary/80 whitespace-nowrap"
                onClick={() => onViewDiff(version)}
              >
                View diff
              </button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <button
                    type="button"
                    className="text-muted-foreground hover:text-destructive transition-colors shrink-0"
                    title="Delete version"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete this version?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This will permanently delete version &quot;{version.name}&quot;. This action cannot be undone.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={() => onDeleteVersion(version)}
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    >
                      Delete
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
